import React from 'react';
import { CAN_COUNTRY_ISO3CODE, USA_COUNTRY_ISO3CODE } from '../constants';

const joinAddressParts = (parts) => parts.filter(Boolean).join(', ');

const withTrailingCommas = (lines) =>
  lines.map((line, index) => (index < lines.length - 1 ? `${line},` : line));

// Multi-line display: street, second line, "city, state, zip code" and the
// country each on their own line, every line but the last ending with a comma.
const buildAddressBlock = ({
  AddressLine,
  AddressLine2,
  City,
  StateCaption,
  PostalCode,
  CountryIso3Code,
}) => {
  const lines = [
    AddressLine,
    AddressLine2,
    joinAddressParts([City, StateCaption, PostalCode]),
    CountryIso3Code,
  ].filter(Boolean);

  return withTrailingCommas(lines).join('\n');
};

export const formatAddress = (address) => {
  if (!address) {
    return 'Loading...';
  }

  return <span>{buildAddressBlock(address)}</span>;
};

export const formatAddressString = (address) => {
  if (!address) {
    return null;
  }

  return buildAddressBlock(address);
};

// Street, line 2, city, state, zip code, country (country omitted for the USA)
export const formatAddressStringInline = (address, countries) => {
  if (!address) {
    return null;
  }

  const {
    AddressLine,
    AddressLine2,
    AptOrSuite,
    City,
    StateRegion,
    PostalCode,
    CountryIso3Code,
  } = address;

  const countryName =
    CountryIso3Code && CountryIso3Code !== USA_COUNTRY_ISO3CODE
      ? getCountryNameByIso3(countries, CountryIso3Code)
      : '';

  return joinAddressParts([
    AddressLine,
    AddressLine2,
    AptOrSuite,
    City,
    StateRegion,
    PostalCode,
    countryName,
  ]);
};

// Same order as formatAddressStringInline, but always includes the country
export const formatAddressStringInlineWithCountry = (address, countries) => {
  if (!address) {
    return null;
  }

  const {
    AddressLine,
    AddressLine2,
    AptOrSuite,
    City,
    StateRegion,
    PostalCode,
    CountryIso3Code,
  } = address;

  return joinAddressParts([
    AddressLine,
    AddressLine2,
    AptOrSuite,
    City,
    StateRegion,
    PostalCode,
    getCountryNameByIso3(countries, CountryIso3Code),
  ]);
};

// Display lines: "street, line 2", "city, state, zip code", then the country,
// every line but the last ending with a comma.
export const formatAddressLines = (address, countries) => {
  if (!address) {
    return [];
  }

  const {
    AddressLine,
    AddressLine2,
    AptOrSuite,
    City,
    StateRegion,
    PostalCode,
    CountryIso3Code,
  } = address;

  const lines = [
    joinAddressParts([AddressLine, AddressLine2, AptOrSuite]),
    joinAddressParts([City, StateRegion, PostalCode]),
    getCountryNameByIso3(countries, CountryIso3Code),
  ].filter(Boolean);

  return withTrailingCommas(lines);
};

export function getCountryStates(countryCode, countyField) {
  return (
    countryCode === USA_COUNTRY_ISO3CODE ||
    countryCode === CAN_COUNTRY_ISO3CODE ||
    countyField === USA_COUNTRY_ISO3CODE ||
    countyField === CAN_COUNTRY_ISO3CODE
  );
}

export const getStatesOptions = (states = []) => [
  ...states.map((state) => ({
    value: state.Code,
    label: state.Caption,
  })),
];

export const formatMailingAddress = (address, bankName) => {
  const {
    Receipient,
    Attn,
    AddressLine,
    AddressLine2,
    City,
    StateCaption,
    PostalCode,
  } = address;
  return `${Receipient ? `${Receipient}` : ''}${bankName ? `${bankName}` : ''}${
    Attn ? `\n Attn: ${Attn}` : ''
  }${AddressLine ? `\n ${AddressLine}` : ''}${
    AddressLine2 ? `\n ${AddressLine2}` : ''
  }${City ? `\n ${City}, ` : ``}${StateCaption ? `${StateCaption} ` : ''}${
    PostalCode ? `${PostalCode}` : ''
  }`;
};

export const getCountryNameByIso3 = (countries, iso3) =>
  countries?.find((c) => c.Iso3Code === iso3)?.Caption || '';
